/**
 * Insight crawler: scans enriched transaction rows and surfaces a small set of
 * ranked, actionable insights (trends, concentration, ROI vs volume, etc.).
 */
import type { Transaction } from "../types";
import type { ActionableInsight } from "../types/insights";

function num(v: unknown): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/[$,]/g, ""));
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function soldRows(rows: Transaction[]): Transaction[] {
  return rows.filter((r) => r.sale_date && String(r.sale_date).trim() !== "");
}

function groupSoldBy(
  rows: Transaction[],
  key: keyof Transaction
): Map<string, { count: number; profit: number; revenue: number; cost: number }> {
  const m = new Map<string, { count: number; profit: number; revenue: number; cost: number }>();
  for (const r of rows) {
    const raw = r[key];
    const label =
      raw !== null && raw !== undefined && String(raw).trim() !== ""
        ? String(raw).trim()
        : "Unknown";
    const cur = m.get(label) || { count: 0, profit: 0, revenue: 0, cost: 0 };
    cur.count += 1;
    cur.profit += num(r.calculated_net_profit);
    cur.revenue += num(r.sale_price);
    cur.cost += num(r.calculated_total_cost ?? r.cost);
    m.set(label, cur);
  }
  return m;
}

function insight(
  id: string,
  title: string,
  summary: string,
  actions: string[],
  severity: ActionableInsight["severity"],
  meta?: ActionableInsight["meta"]
): ActionableInsight {
  return { id, title, summary, actions, severity, meta };
}

/**
 * Run the crawler over enriched transactions (joined + CalculationService.enrichTransactions).
 */
export function runInsightCrawler(transactions: Transaction[]): ActionableInsight[] {
  const sold = soldRows(transactions);
  const insights: ActionableInsight[] = [];

  if (sold.length === 0) {
    insights.push(
      insight(
        "no-sales",
        "No completed sales in your data yet",
        "Insights that compare ROI, profit, and volume need at least a few sold lines with sale dates and prices.",
        [
          "Record sale dates and sale prices when you move inventory.",
          "After a handful of sales, re-run the crawler to see ROI and concentration patterns.",
        ],
        "neutral"
      )
    );
    return insights;
  }

  const totalProfit = sold.reduce((s, r) => s + num(r.calculated_net_profit), 0);
  const winRate =
    sold.length > 0 ? sold.filter((r) => num(r.calculated_net_profit) > 0).length / sold.length : 0;

  if (totalProfit > 0 && winRate < 0.45) {
    insights.push(
      insight(
        "win-rate",
        "Many sales are thin or negative vs a positive total",
        `About ${Math.round(winRate * 100)}% of sold lines show positive net profit, while your total net is still positive — often a sign of a few big wins carrying the rest.`,
      [
        "List your last 10 sales sorted by net profit and review the losers: fees, comps, or purchase price?",
        "Tighten purchase discipline on sets or parallels that repeatedly show red.",
        "Double down on the buyer channels and card types that show repeatable green.",
      ],
      "warning",
      { soldLines: sold.length, winRatePct: Math.round(winRate * 100) }
    ));
  }

  // High ROI + low volume by set (example from product spec)
  const bySet = groupSoldBy(sold, "set");
  const roiRows: { key: string; roi: number; count: number; profit: number }[] = [];
  Array.from(bySet.entries()).forEach(([key, agg]) => {
    if (agg.count < 1 || agg.cost <= 0) return;
    const roi = (agg.profit / agg.cost) * 100;
    roiRows.push({ key, roi, count: agg.count, profit: agg.profit });
  });
  roiRows.sort((a, b) => b.roi - a.roi);
  const candidate = roiRows.find((r) => r.roi >= 40 && r.count <= 5 && r.profit > 0);
  if (candidate) {
    insights.push(
      insight(
        "high-roi-low-volume-set",
        `Strong ROI on "${candidate.key}" but low sale volume`,
        `This set shows roughly ${candidate.roi.toFixed(0)}% ROI on ${candidate.count} sold card(s). That is a strong signal you may be under-scaling a winning lane.`,
      [
        `Increase buy frequency for "${candidate.key}" while keeping the same grading and sale channels that produced this ROI.`,
        "Clone this playbook to adjacent parallels or inserts from the same product year.",
        "Cap position size per card until you confirm liquidity — scale volume, not single-card risk.",
      ],
      "positive",
      { set: candidate.key, roiPct: Math.round(candidate.roi), soldCount: candidate.count }
    ));
  }

  const pushRoiVolume = (
    id: string,
    dimensionLabel: string,
    keyField: keyof Transaction,
    byDim: Map<string, { count: number; profit: number; revenue: number; cost: number }>
  ) => {
    const rows: { key: string; roi: number; count: number; profit: number }[] = [];
    Array.from(byDim.entries()).forEach(([key, agg]) => {
      if (agg.count < 1 || agg.cost <= 0 || key === "Unknown") return;
      const roi = (agg.profit / agg.cost) * 100;
      rows.push({ key, roi, count: agg.count, profit: agg.profit });
    });
    rows.sort((a, b) => b.roi - a.roi);
    const pick = rows.find((r) => r.roi >= 35 && r.count <= 4 && r.profit > 0);
    if (!pick) return;
    insights.push(
      insight(
        id,
        `Strong ROI on ${dimensionLabel} "${pick.key}" with limited volume`,
        `Roughly ${pick.roi.toFixed(0)}% ROI across ${pick.count} sold line(s). Consider scaling what already works.`,
      [
        `Source more inventory in this ${dimensionLabel.toLowerCase()} using the same buy criteria that produced these sales.`,
        "Batch grading and list on the same platforms that cleared these winners.",
        "Track sell-through days — if liquidity is strong, increase ticket size gradually.",
      ],
      "positive",
      { [keyField]: pick.key, roiPct: Math.round(pick.roi), soldCount: pick.count } as Record<string, string | number>
    ));
  };

  pushRoiVolume("high-roi-low-volume-subject", "player / subject", "subject", groupSoldBy(sold, "subject"));
  pushRoiVolume("high-roi-low-volume-parallel", "parallel", "parallel", groupSoldBy(sold, "parallel"));

  // Concentration: single sport dominates profit
  const bySport = groupSoldBy(sold, "sport");
  let topSport = "";
  let topSportProfit = 0;
  Array.from(bySport.entries()).forEach(([k, v]) => {
    if (v.profit > topSportProfit) {
      topSportProfit = v.profit;
      topSport = k;
    }
  });
  if (topSport && totalProfit > 0 && topSportProfit / totalProfit >= 0.55 && bySport.size >= 2) {
    insights.push(
      insight(
        "sport-concentration",
        `Profit is concentrated in ${topSport}`,
        `Roughly ${Math.round((topSportProfit / totalProfit) * 100)}% of net profit came from ${topSport}. Concentration is fine if intentional — but it increases exposure to one market move.`,
      [
        "If intentional: document what works in that sport and clone sourcing and grading workflows.",
        "If not: pilot one adjacent sport with small tickets using the same sell-through channels.",
        "Track monthly profit by sport so you can rebalance before a slow quarter.",
      ],
      "neutral",
      { sport: topSport, profitSharePct: Math.round((topSportProfit / totalProfit) * 100) }
    ));
  }

  // Trend: last 90d vs prior 90d profit (simple)
  const now = Date.now();
  const ms90 = 90 * 24 * 60 * 60 * 1000;
  const recent = sold.filter((r) => {
    const t = r.sale_date ? new Date(r.sale_date).getTime() : 0;
    return t && now - t <= ms90;
  });
  const older = sold.filter((r) => {
    const t = r.sale_date ? new Date(r.sale_date).getTime() : 0;
    return t && now - t > ms90 && now - t <= 2 * ms90;
  });
  const pRecent = recent.reduce((s, r) => s + num(r.calculated_net_profit), 0);
  const pOlder = older.reduce((s, r) => s + num(r.calculated_net_profit), 0);
  if (older.length >= 3 && recent.length >= 3) {
    const change = pOlder !== 0 ? ((pRecent - pOlder) / Math.abs(pOlder)) * 100 : 0;
    if (change <= -25) {
      insights.push(
        insight(
          "profit-trend-down",
          "Net profit in the last 90 days is down vs the prior 90 days",
          "A material drop in rolling profit usually comes from mix (cheaper inventory), fees, or slower sell-through — not one bad day.",
        [
          "Compare average sale price and average net profit per sold card between the two windows.",
          "Check grading turnaround and fee drag — delayed returns can push sales into the wrong window.",
          "Revisit buy boxes: temporarily tighten max cost until rolling profit stabilizes.",
        ],
        "warning",
        { changePct: Math.round(change) }
      ));
    } else if (change >= 25) {
      insights.push(
        insight(
          "profit-trend-up",
          "Net profit in the last 90 days is up vs the prior 90 days",
          "Momentum is good — lock in what changed (inventory mix, grading speed, or sale channel).",
        [
          "Identify the top 3 sets or subjects driving the lift and increase sourcing depth there.",
          "Avoid overpaying on momentum — keep buy rules tied to expected net, not hype.",
          "Reinvest a portion of the lift into faster sell-through (better photos, faster grading batch).",
        ],
        "positive",
        { changePct: Math.round(change) }
      ));
    }
  }

  // Fallback if very few insights
  if (insights.length === 0) {
    insights.push(
      insight(
        "healthy-baseline",
        "No strong risk flags from this pass",
        "Your sold inventory did not trigger high-priority patterns in this crawl. Re-run after more sales or when you change grading or sourcing mix.",
      [
        "Add more sold lines with accurate costs and fees to unlock ROI-by-dimension insights.",
        "Use saved analyses to track monthly net profit and ROI by set or player.",
      ],
      "neutral",
      { soldLines: sold.length }
    ));
  }

  // Rank: warning first, then positive, then neutral; then by absolute meta profit share if any
  const rank = (s: ActionableInsight["severity"]) =>
    s === "warning" ? 0 : s === "positive" ? 1 : 2;
  insights.sort((a, b) => rank(a.severity) - rank(b.severity));

  return insights.slice(0, 8);
}
